<template>
<div class="container">
	<div class="page page-inventory">
		<!-- Header -->
		<div class="page-header flex-center">
			<h1 class="page-title">{{ $t('Revenue Stream') }}</h1>
		</div>

		<!-- Overviews -->
		<div class="inventory-summaries row">
			<div class="col-md-12">
				<div class="row">
					<div class="col-md-4">
						<div class="box box--primary mh-120">
							<div class="m-2">
								<h4
									class="text-accent font-weight-bold mb-0"
									:class="{ 'is-loading': isFetchingBalance }">
									<span>{{ currency }} {{ balanceDisplay }}</span>
								</h4>
								<span class="d-block mt-2"><i class="bi bi-wallet"></i> {{ $t('Current Balance') }}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="col-md-4">
				<div class="box box--primary mh-120">
					<div class="m-2">
						<h4
							class="text-accent font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingBalanceCommission }">
							<span>{{ currency }} {{ commissionDisplay }}</span>
						</h4>
						<span class="d-block mt-2"><i class="bi bi-wallet"></i> {{ $t('All Commission') }}</span>
					</div>
				</div>
			</div>
			<div class="col-md-4">
				<div class="box box--primary mb-md-0 mh-120">
					<div class="m-2">
						<h4
							class="text-accent font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingBalanceServiceFee }">
							<span>{{ currency }} {{ serviceFeeDisplay }}</span>
						</h4>
						<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('All Service Fee') }}</span>
					</div>
				</div>
			</div>
			<div class="col-md-4">
				<div class="box box--primary mb-md-0 mh-120">
					<div class="m-2">
						<h4
							class="text-accent font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingBalanceProductServiceFee }">
							<span>{{ currency }} {{ productSourcingFeeDisplay }}</span>
						</h4>
						<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('All Product Sourcing Fee') }}</span>
					</div>
				</div>
			</div>
			<div class="col-md-4">
				<div class="box box--primary mb-md-0 mh-120">
					<div class="m-2">
						<h4
							class="text-danger font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingBalanceHandlingFee }">
							<span>{{ currency }} {{ handlingFeeDisplay }}</span>
						</h4>
						<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('All Handling Fee') }}</span>
					</div>
				</div>
			</div>
			<div class="col-md-4">
				<div class="box box--primary mb-md-0 mh-120">
					<div class="m-2">
						<h4
							class="text-accent font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingStash }">
							<span>{{ currency }} {{ stashDisplay }}</span>
						</h4>
						<div class="d-flex align-items-baseline justify-content-between">
							<span class="d-block mt-2"><i class="bi bi-stopwatch"></i> {{ $t('Total Stash') }}</span>
							<router-link
								to="/admin/credit/stash">View All
							</router-link>
						</div>
					</div>
				</div>
			</div>
			<div class="col-md-4">
				<div class="box box--primary mb-md-0 mh-120">
					<div class="m-2">
						<h4
							class="text-accent font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingBalanceRevenueProduct }">
							<span>{{ currency }} {{ revenueProductDisplay }}</span>
						</h4>
						<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('All Revenue Product') }}</span>
					</div>
				</div>
			</div>
		</div>

		<hr />

		<div class="row">
			<div class="mb-4 col-md-12 text-right ml-a">
				<!-- Date Filter -->
				<daterangepicker
					:presets="filterDatePresets"
					:start="since"
					:end="until"
					@selected="setFilterDate">
				</daterangepicker>
			</div>
			<div class="col-md-4 order-sm-1">
				<div class="box box--primary mh-120">
					<div class="m-2">
						<h4
							class="text-accent font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingBalanceByDate }">
							<span>{{ currency }} {{ balanceByDateDisplay }}</span>
						</h4>
						<span class="d-block mt-2"><i class="bi bi-wallet"></i> {{ $t('Total Commission') }}</span>
					</div>
				</div>
			</div>
			<div class="col-md-4 order-sm-2">
				<div class="box box--primary mh-120">
					<div class="m-2">
						<h4
							class="text-accent font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingBalanceByDate }">
							<span>{{ currency }} {{ serviceFeeByDateDisplay }}</span>
						</h4>
						<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('Total Service Fee') }}</span>
					</div>
				</div>
			</div>
			<div class="col-md-4 order-sm-3">
				<div class="box box--primary mh-120">
					<div class="m-2">
						<h4
							class="text-accent font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingBalanceByDate }">
							<span>{{ currency }} {{ productSourcingFeeByDateDisplay }}</span>
						</h4>
						<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('Total Product Sourcing Fee') }}</span>
					</div>
				</div>
			</div>
			<div class="col-md-4 order-sm-4">
				<div class="box box--primary mh-120">
					<div class="m-2">
						<h4
							class="text-danger font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingBalanceByDate }">
							<span>{{ currency }} {{ handlingFeeByDateDisplay }}</span>
						</h4>
						<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('Total Handling Fee') }}</span>
					</div>
				</div>
			</div>
			<div class="col-md-4 order-sm-4">
				<div class="box box--primary mh-120">
					<div class="m-2">
						<h4
							class="text-accent font-weight-bold mb-0"
							:class="{ 'is-loading': isFetchingBalanceByDate }">
							<span>{{ currency }} {{ revenueProductByDateDisplay }}</span>
						</h4>
						<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('Total Revenue Product') }}</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Menu -->
		<div class="table-list-menu d-lg-flex align-items-center">
			<!-- Table Actions -->
			<div
				class="table-actions d-flex align-items-center flex-wrap">
				<!-- Withdraw -->
				<button
					type="button"
					class="btn btn-success mr-3"
					v-if="isCanWithdraw && !isWdDisabled"
					@click="showWithdraw">
					<span>{{ $t('Withdraw') }}</span>
				</button>

				<button
					type="button"
					class="btn btn-success mr-3 disabled"
					v-if="isWdDisabled"
					v-tooltip="'Withdraw is currently disabled for your account. Please contact our Customer Support'">
					<span>{{ $t('Withdraw') }}</span>
				</button>
			</div>
			<!-- Table Filters -->
			<div class="table-filters d-flex align-items-center ml-a hidden">
				<!-- Date Filter -->
				<daterangepicker
					:presets="filterDatePresets"
					:start="since"
					:end="until"
					@selected="setFilterDate">
				</daterangepicker>
			</div>
		</div>

		<!-- Main Table -->
		<div class="card card-body border-0">
			<div class="d-flex flex-wrap">
				<div class="form-group">
					<input type="text" class="input form-control" placeholder="Search Order ID" v-model="keyword" @input="onKeywordChange">
				</div>

				<!-- Actions -->
				<div class="export ml-auto mb-2">
					<!-- Clear Filter -->
					<button
						type="button"
						class="btn btn-plain mr-2"
						@click="clearFilter"
						v-if="isFilterOn"
					>
						{{ $t('Clear Filter') }}
					</button>

					<!-- Filter -->
					<button
						type="button"
						class="filter-toggle btn btn-light btn-icon mr-2"
						@click="isShowFilter = true"
					>
						<i class="material-icons mr-1">filter_list</i>
						<span class="mr-2">{{ $t('Filter') }}</span>
					</button>

					<!-- Export -->
					<button
						type="button"
						class="btn btn-primary"
						:class="{ 'is-loading': isExporting }"
						@click="exportRevenue('outsource_commission,outsource_service,product_sourcing,revenue_product,other')"
					>
						<i class="material-icons">save_alt</i>
						<span class="mr-2">{{ $t('Export') }}</span>
					</button>
				</div>
			</div>
            <div class="wallet-history-table table-responsive">
                <table class="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th width="5%">{{ $t('No') }}.</th>
                            <th width="30%">{{ $t('Description') }}</th>
                            <th>{{ $t('Value') }}</th>
                            <th width="12%">{{ $t('Status') }}</th>
                            <th>{{ $t('Type') }}</th>
                            <th>{{ $t('Date') }}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <template
                            v-for="(act, index) in histories">
                            <tr
                                class="cur-p"
                                :class="{ 'highlighted': history && history.id === act.id }"
                                :key="`${index}-1`"
                                @click="showDetails(act)">
                                <!-- Index -->
                                <td class="text-right">{{ act.id }}.</td>

                                <!-- Description -->
                                <td>
                                    <span v-if="act.description && act.description !== ''">
                                        {{ act.description }}
                                    </span>
                                    <span v-if="act.description === ''">-</span>
                                </td>

                                <!-- Value -->
                                <td class="text-right">
                                    <mutation-value :data="act" :key="act.id" />
                                </td>

                                <!-- Changed by -->
                                <!-- <td>
                                    <span>{{ getMemberName(act.created_by) }}</span>
                                </td> -->

                                <!-- Status -->
                                <td class="text-center">
                                    <mark :class="`order-status status-${act.status}`"><span>{{ act.status }}</span></mark>
                                </td>

                                <!-- Type -->
                                <td class="text-left">
                                    <span>{{ getTypeLabel(act.type) }}</span>
                                </td>

                                <!-- Date -->
                                <td class="text-center">
                                    <span>{{ formatDate(act.created_at) }}</span>
                                </td>

                                <!-- Actions -->
                                <td class="text-center">
                                    <div class="d-flex align-items-center justify-content-end pr-4">
                                        <wallet-history-metas
                                            class="mr-3"
                                            :show-title="false"
                                            :metas="act.metas"
                                            v-if="act.metas"
                                        />
                                        <i class="material-icons cur-p" v-tooltip="'Show Details'">
                                            <template v-if="history && history.id === act.id">keyboard_arrow_up</template>
                                            <template v-else>keyboard_arrow_down</template>
                                        </i>
                                    </div>
                                </td>
                            </tr>
                            <tr
                                class="detail-view"
                                :key="`${index}-2`"
                                v-if="history && history.id === act.id">
                                <td colspan="7">
                                    <template v-if="act.details">
                                        <div
                                            class="text-center"
                                            v-if="act.details.length === 0">
                                            - {{ $t('No Commission Details') }} -
                                        </div>
                                        <!-- Details -->
                                        <div class="p-1" v-else>
                                            <h6 class="mb-3 text-bold">{{ $t('Commission Details') }}</h6>
                                            <div class="card card-body p-3">
                                                <wallet-history-details :details="act.details" />
                                            </div>
                                        </div>
                                    </template>
                                    <wallet-history-metas :metas="act.metas" v-if="act.metas" />
                                    <div class="is-loading dark" v-if="!act.details || !act.metas"></div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

		<!-- Table Loader -->
		<div
			class="table-fetch-status"
			v-if="isFetching">
			<div class="is-loading large dark m-auto"></div>
		</div>

		<!-- Table Empty -->
		<div
			class="list-empty border-0"
			v-if="histories.length === 0 && isFetching === false">
			<span><i class="material-icons md-48 mr-2">error_outline</i></span>
			<span v-if="keyword === ''">There is no data in this date range.</span>
			<span v-else>No activities was found</span>
		</div>
	</div>

	<!-- Modals -->
	<div class="modals">
		<!-- Withdraw -->
		<modal
			class="withdraw-modal"
			size="medium"
			@close="isWithdrawalVisible = false"
			v-if="isWithdrawalVisible">
			<div slot="header">
				<h3>Withdraw</h3>
			</div>
			<template slot="body">
				<template v-if="!isOtpVisible">
					<div class="callout callout--info mb-3 bg-blue-light">
						<ul class="mb-0 pl-3">
							<li><p class="mb-1">{{ $t('Withdraw hanya dapat dilakukan maksimal 1x per hari') }}.</p></li>
							<li><p class="mb-1">{{ $t('Dan akan diproses di hari kerja pada jam 09:00 - 17:00 WIB') }}.</p></li>
							<li><p class="mb-0">{{ $t('Direkomendasikan Withdraw ke Rekening BCA, Selain rekening BCA akan memakan waktu 2-3 hari kerja') }}</p></li>
						</ul>
					</div>
					<!-- Value -->
					<div class="form-group">
						<label class="text-bold">{{ $t('How much you want to withdraw') }}?</label>
						<price-display
							class="form-control"
							:class="{ 'is-error': !isWithdrawValueValid }"
							:placeholder="withdrawTreshold"
							:min="withdrawTreshold"
							:max="balance"
							:disabled="balance === 0"
							v-model.number="withdrawValue">
						</price-display>
						<div class="mt-2 text-italic">
							<!-- <span class="mr-2">Minimal: <b>{{ formatNumber(withdrawTreshold) }}</b></span> -->
							<span class="text-primary cur-p" @click="setWithdrawValue(balance)">Maximal: <b>{{ balanceDisplay }}</b></span>
						</div>
						<!-- <input
							type="number"
							class="form-control"
							placeholder="1"
							min="1"
							:class="{ 'is-error': !isWithdrawValueValid}"
							v-model.number="withdrawValue"> -->
					</div>
					<!-- Withdraw to -->
					<div class="form-group">
						<label class="text-bold">{{ $t('Withdraw to') }}:</label>
						<multiselect
							class="form-control"
							placeholder="Select Bank Account to receive the withdrawal"
							label="text"
							track-by="id"
							:options="bankAccountsOptions"
							:preselect-first="true"
							:allow-empty="false"
							:show-labels="false"
							v-model="bankAccount">
						</multiselect>
					</div>
					<div class="form-group">
						<label class="text-bold">Password</label>
						<input
							type="password"
							class="form-control"
							placeholder="Please input your account password"
							v-model="password"
						>
					</div>
				</template>
				<div v-if="isOtpVisible">
					<h4>{{ $t('Verfikasi penarikan dana kamu') }}</h4>
					<div class="text-secondary mb-4">
						Masukkan 6 digit kode yang dikirim ke email {{ sensoredEmail }}
					</div>
					<div class="form-group">
						<input
							type="number"
							class="form-control"
							size="6"
							v-model="otpCode"
						>
					</div>
					<!-- Re-Send -->
					<div>
						<button
							class="btn btn-link px-0 btn-regenerate-otp"
							:class="{ 'text-grey-dark': otpTimerValue !== 0, 'is-loading': isGeneratingOtp }"
							:disabled="otpTimerValue !== 0"
							@click="generateOtp">
							Kirim ulang kode ({{ otpTimer }})
						</button>
					</div>
				</div>
			</template>
			<template slot="footer">
				<!-- Cancel -->
				<button
					type="button"
					class="btn btn-light"
					@click="cancelWithdraw">
					{{ $t('Cancel') }}
				</button>
				<!-- Submit -->
				<button
					class="btn btn-success"
					:class="{ 'is-loading': isGeneratingOtp || isCheckingPassword }"
					:disabled="isGeneratingOtp || isCheckingPassword"
					@click="checkPassword"
					v-if="!isOtpVisible">
					<!-- <i class="material-icons">save</i> -->
					<span>{{ $t('Withdraw') }}</span>
				</button>
				<!-- Submit -->
				<button
					class="btn btn-success"
					:class="{ 'is-loading': isWithdrawing }"
					:disabled="!isWithdrawValid || isWithdrawing || !isOtpValid"
					@click="validateOtp"
					v-if="isOtpVisible">
					<!-- <i class="material-icons">save</i> -->
					<span>{{ $t('Validate OTP') }}</span>
				</button>
				<!-- Submit -->
				<!-- <button
					class="btn btn-success"
					:class="{ 'is-loading': isWithdrawing }"
					:disabled="!isWithdrawValid || isWithdrawing"
					@click="withdraw"
					v-if="!isAdmin">
					<span>{{ $t('Withdraw') }}</span>
				</button> -->
			</template>
		</modal>

		<!-- Table Filter -->
		<modal
			class="table-filter-modal"
			size="medium"
			@close="isShowFilter = false"
			v-if="isShowFilter">
			<div slot="header">
				<h3><i class="material-icons">filter_list</i>Filter</h3>
			</div>
			<div slot="body">
				<!-- Type -->
				<div class="form-group">
					<label><b>Type</b></label>
					<select class="form-control" v-model="type">
						<option value="all">All</option>
						<option value="handling_fee">Handling Fee</option>
						<option value="outsource_commission">Outsource Commission</option>
						<option value="outsource_payment_product">Outsource Payment Product</option>
						<option value="outsource_service">Outsource Service</option>
						<option value="product_sourcing">Product Sourcing</option>
						<option value="payment_product_wholesale">Payment Product Wholesale</option>
						<option value="revenue_product">Revenue Product</option>
						<option value="other">Other</option>
					</select>
				</div>

				<!-- Status -->
				<div class="form-group">
					<label><b>Status</b></label>
					<select class="form-control" v-model="status">
						<option value="all">All</option>
						<option value="pending">Pending</option>
						<option value="completed">Completed</option>
						<option value="cancelled">Cancelled</option>
					</select>
				</div>
			</div>
			<div slot="footer">
				<button type="button" class="btn btn-light" @click="isShowFilter = false">{{ $t('Close') }}</button>
				<button type="button" class="btn btn-primary" @click="applyFilter">Apply</button>
			</div>
		</modal>
	</div>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import dayjs from 'dayjs';
import XLSX from 'xlsx';
import Modal from '@/components/Modal.vue';
import Daterangepicker from '@/components/Daterangepicker.vue';
import PriceDisplay from '@/components/PriceDisplay.vue';
import WalletHistoryDetails from '@/components/WalletHistoryDetails.vue';
import WalletHistoryMetas from '@/components/WalletHistoryMetas.vue';
import MutationValue from '@/components/MutationValue.vue';
import {
	duplicateVar,
	getDateRange,
	downloadFile,
	getAxiosErrorMessage,
	formatNumberPlain,
	sensorEmailAddress, generateTimerLabel, toTitleCase } from '@/lib/helper';
import walletApi from '@/api/wallet';
import walletStashApi from '@/api/wallet-stash';
import otpApi from '@/api/otp';
import user from '@/api/user';
import statsApi from '@/api/statistic';

const defaultFilterDate = getDateRange();

export default {
	name: 'AdminCommission',
	components: {
		Modal,
		Daterangepicker,
		PriceDisplay,
		WalletHistoryDetails,
		WalletHistoryMetas,
		MutationValue,
	},
	data() {
		return {
			keyword: '',
			limit: 25,
			page: 1,
			sort_by: 'id',
			sort: 'desc',
			histories: [],
			history: null,
			isFetching: false,
			isAll: false,
			filterDate: defaultFilterDate,
			isWithdrawalVisible: false,
			isWithdrawing: false,
			isExporting: false,
			withdrawValue: 1,
			withdrawNotes: '',
			currency: 'Rp',
			isFetchingBalance: false,
			isFetchingBalanceByDate: false,
			isFetchingBalanceServiceFee: false,
			isFetchingBalanceProductServiceFee: false,
			isFetchingBalanceRevenueProduct: false,
			isFetchingBalanceCommission: false,
			isFetchingBalanceHandlingFee: false,
			commission: 0,
			handlingFee: 0,
			handlingFeeByDate: 0,
			balance: 0,
			balanceByDate: 0,
			serviceFee: 0,
			serviceFeeByDate: 0,
			productSourcingFee: 0,
			productSourcingFeeByDate: 0,
			revenueProduct: 0,
			revenueProductByDate: 0,
			withdrawn: 0,
			withdrawTreshold: 0,
			bankAccount: null,
			password: '',
			isShowFilter: false,
			status: 'all',
			type: 'all',
			stashTotal: 0,
			isFetchingStash: false,
			// isOtpValid: false,
			isGeneratingOtp: false,
			isOtpVisible: false,
			otpCode: '',
			otpTimerValue: 300,
			otpTimerInterval: null,
			maxOtpTime: 300,
			isCheckingPassword: false,
		};
	},
	methods: {
		...mapActions({
		}),
		resetHistories() {
			this.histories = [];
			this.isAll = false;
			this.page = 1;
		},
		resetFilter() {
			this.status = 'all';
			this.type = 'all';
		},
		clearFilter() {
			this.resetFilter();
			this.applyFilter();
		},
		applyFilter() {
			this.fetchHistories(true);
		},
		fetchHistories(reset = true, includeDate = true) {
			this.isFetching = true;
			if (reset) {
				this.resetHistories();
			}
			const params = this.getFetchParams(includeDate);
			const callback = (response) => {
				const histories = response.data.data;
				histories.forEach((history) => {
					this.histories.push(history);
				});
				this.page++;
				this.isShowFilter = false;
				if (histories.length < this.limit) {
					this.isAll = true;
				}
				this.isFetching = false;
			};
			const errorCallback = () => {
				this.isFetching = false;
				this.isAll = true;
			};
			walletApi.getHistories(params, callback, errorCallback);
		},
		init() {
			// this.checkCapabilities();
			this.initFilterDate();
			this.getBalance();
			this.getServiceFee();
			this.getRevenueProduct();
			this.getProductServiceFee();
			this.getCommission();
			this.getHandlingFee();
			this.getStashBalance();
			this.fetchHistories();
			this.fetchDataStats();
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const since = dayjs(filterDate.start).format('YYYY-MM-DD');
			const until = dayjs(filterDate.end).format('YYYY-MM-DD');
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchHistories(true);
			this.fetchDataStats();
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				if (this.keyword !== '') {
					self.fetchHistories(true, false);
				} else {
					self.fetchHistories(true);
				}
			}, 1000);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchHistories(false);
				}
			}
		},
		changeSorting(key) {
			const isSame = this.sort_by === key;
			this.sort_by = key;
			const sort = isSame && this.sort === 'asc' ? 'desc' : 'asc';
			this.sort = sort;
			// Re-fetch
			this.fetchHistories(true);
		},
		sortClass(key) {
			const sameKey = this.sort_by === key;
			const className = sameKey ? `th-sort-${this.sort}` : '';
			return className;
		},
		checkCapabilities() {
			if (!this.isOwner && !this.isUserCan('wallet_view')) {
				this.$router.push('/');
			} else {
				this.isRouteChecked = true;
			}
		},
		formatDate(dateString) {
			const format = 'DD-MM-YYYY - HH:mm';
			const timezone = this.user.timezone;
			const formattedDate = this.moment.utc(dateString).tz(timezone).format(format);
			return dateString && dateString !== '' ? formattedDate : '';
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const format = 'YYYY-MM-DD';
				const since = dayjs(this.since).format(format);
				const until = dayjs(this.until).format(format);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		getMemberName(id) {
			const member = this.members.find(member => member._id === id);
			return member ? member.name : '';
		},
		getFetchParams(includeDate = true) {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
			};

			const keyword = this.keyword.trim();
			if (keyword && keyword !== '') {
				params.order_id = keyword;
			}
			if (includeDate) {
				params.since = startDate;
				params.until = endDate;
			}

			if (this.status !== 'all') {
				params.status = this.status;
			}

			if (this.type !== 'all') {
				params.type = this.type;
			}

			return params;
		},
		getBalance() {
			this.isFetchingBalance = true;
			const callback = (response) => {
				this.isFetchingBalance = false;
				const wallet = response.data;
				this.balance = wallet.balance;
			};
			const errorCallback = (e) => {
				this.isFetchingBalance = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Balance',
					text: message,
				});
			};
			walletApi.getBalance(callback, errorCallback);
		},
		getServiceFee() {
			this.isFetchingBalanceServiceFee = true;
			const callback = (response) => {
				this.isFetchingBalanceServiceFee = false;
				this.serviceFee = response.data?.data;
			};
			const errorCallback = (e) => {
				this.isFetchingBalanceServiceFee = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Balance',
					text: message,
				});
			};
			walletApi.getServiceFee(callback, errorCallback);
		},
		getRevenueProduct() {
			this.isFetchingBalanceRevenueProduct = true;
			const callback = (response) => {
				this.isFetchingBalanceRevenueProduct = false;
				this.revenueProduct = response.data?.data;
			};
			const errorCallback = (e) => {
				this.isFetchingBalanceRevenueProduct = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Balance',
					text: message,
				});
			};
			walletApi.getRevenueProduct(callback, errorCallback);
		},
		getProductServiceFee() {
			this.isFetchingBalanceProductServiceFee = true;
			const callback = (response) => {
				this.isFetchingBalanceProductServiceFee = false;
				this.productSourcingFee = response.data?.data;
			};
			const errorCallback = (e) => {
				this.isFetchingBalanceProductServiceFee = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Balance',
					text: message,
				});
			};
			walletApi.getProductSourcingFee(callback, errorCallback);
		},
		getCommission() {
			this.isFetchingBalanceCommission = true;
			const callback = (response) => {
				this.isFetchingBalanceCommission = false;
				this.commission = response.data?.data;
			};
			const errorCallback = (e) => {
				this.isFetchingBalanceCommission = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Commission',
					text: message,
				});
			};
			walletApi.getCommission(callback, errorCallback);
		},
		getHandlingFee() {
			this.isFetchingBalanceHandlingFee = true;
			const callback = (response) => {
				this.isFetchingBalanceHandlingFee = false;
				this.handlingFee = response.data?.data;
			};
			const errorCallback = (e) => {
				this.isFetchingBalanceHandlingFee = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Handling Fee',
					text: message,
				});
			};
			walletApi.getHandlingFee(callback, errorCallback);
		},
		withdraw() {
			if (this.isWithdrawing) {
				return;
			}
			this.isWithdrawing = true;
			const value = parseInt(this.withdrawValue);
			const description = this.withdrawDescription;

			const metas = [
				{ key: 'bank', value: this.bankAccount.label },
				{ key: 'account_number', value: this.bankAccount.account_number },
				{ key: 'account_name', value: this.bankAccount.account_name },
			];
			const metasString = JSON.stringify(metas);

			const params = {
				value,
				description,
				metas: metasString,
				password: this.password,
			};

			const callback = (response) => {
				this.isWithdrawing = false;
				const message = response.message;
				if (response.error === 0) {
					this.balance -= value;
					this.withdrawn = parseInt(this.withdrawn) + value;

					const history = response.data;
					this.histories.unshift(history);

					this.$notify({
						group: 'app',
						type: 'success',
						title: 'Withdraw Request Success',
						text: message,
					});
					this.withdrawValue = 0;
					// Hide popup
					this.isWithdrawalVisible = false;

					this.resetOtp();
				} else {
					this.$notify({
						group: 'app',
						type: 'error',
						title: 'Withdraw Request Failed',
						text: message,
					});
				}
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.isWithdrawing = false;
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Withdraw Request Failed',
					text: message,
				});
			};
			walletApi.withdraw(params, callback, errorCallback);
		},
		exportRevenue(type = 'cod_revenue') {
			this.isExporting = true;
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				sort_by: this.sort_by,
				sort: this.sort,
				since: startDate,
				until: endDate,
				type,
			};
			if (this.type !== 'all') {
				params.type = this.type;
			}
			if (this.status !== 'all') {
				params.status = this.status;
			}
			const callback = (response) => {
				const url = response.data;
				if (url && url !== '') {
					downloadFile(url);
				} else {
					const message = 'No transactions with the selected type';
					this.$notify({
						group: 'app',
						type: 'warn',
						title: 'Export Transactions',
						text: message,
					});
				}
				this.isExporting = false;
			};
			const errorCallback = () => {
				this.isExporting = false;
			};
			walletApi.exportHistories(params, callback, errorCallback);
		},
		showWithdraw() {
			this.withdrawValue = Number(this.balance);
			this.isWithdrawalVisible = true;
		},
		formatNumber(number) {
			return formatNumberPlain(number);
		},
		showDetails(history) {
			if (this.history && this.history.id === history.id) {
				this.history = null;
				return;
			}
			this.history = duplicateVar(history);
			if (!history.details) {
				const callback = (response) => {
					const historyItem = response.data;
					this.history = historyItem;
					this.updateHistory(historyItem);
				};
				const errorCallback = () => {};
				walletApi.getHistory(history.id, callback, errorCallback);
			}
		},
		updateHistory(history) {
			const index = this.histories.findIndex(({ id }) => id === history.id);
			if (index !== -1) {
				this.$set(this.histories, index, history);
			}
		},
		setWithdrawValue(value) {
			this.withdrawValue = Number(value);
		},
		exportHistories() {
			const creditHistories = this.histories.filter(({ mutation }) => mutation === 'credit');
			const codFeePercentage = 3;
			let totalGoodsValue = 0;
			let totalShippingCost = 0;
			let totalCodFee = 0;
			let totalAmount = 0;
			let totalFinalAmount = 0;
			const mapper = (historyItem, index) => {
				const number = index + 1;
				const details = historyItem.details;
				const detailTotalPrice = details.find(({ type }) => type === 'total_price');
				const detailShippingCost = details.find(({ type }) => type === 'shipping_cost');
				const detailCodFee = details.find(({ type }) => type === 'cod_fee');
				const amountReceived = detailTotalPrice ? detailTotalPrice.value : 0;
				const shippingCost = detailShippingCost ? detailShippingCost.value : 0;
				const codFee = detailCodFee ? detailCodFee.value : 0;
				const goodsValue = amountReceived - shippingCost;
				const finalAmount = historyItem.value;
				const createdAt = dayjs(historyItem.created_at).format('DD-MM-YYYY HH:mm');

				const historyData = [];
				// No.
				historyData.push(number);
				// Trx ID
				historyData.push(historyItem.id);
				// Created Date
				historyData.push(createdAt);
				// Description
				historyData.push(historyItem.description);
				// Product Price / Goods Value
				historyData.push(goodsValue);
				// Shipping Cost
				historyData.push(shippingCost);
				// Amount Received
				historyData.push(amountReceived);
				// COD Fee Percentage
				historyData.push(`${codFeePercentage}% from Goods Value`);
				// COD Fee
				historyData.push(codFee);
				// Final Amount
				historyData.push(finalAmount);

				totalGoodsValue += goodsValue;
				totalShippingCost += shippingCost;
				totalCodFee += codFee;
				totalAmount += amountReceived;
				totalFinalAmount += finalAmount;

				return historyData;
			};
			const histories = creditHistories.map(mapper);

			// Header
			const header = ['No.', 'Transaction ID', 'Created Date', 'Description', 'Goods Value', 'Shipping Charge', 'Amount Received', 'COD Fee (%)', 'COD Fee (Rp)', 'Total Amount - COD Fee - Shipping Charge'];
			histories.unshift(header);

			const summaryRow = [
				'', // No
				'', // Trx ID
				'', // Created Date
				'', // Description
				totalGoodsValue, // Goods Value
				totalShippingCost, // Shipping Charge
				totalAmount, // Amount Received
				'', // COD Fee %
				totalCodFee, // COD Fee (Rp)
				totalFinalAmount, // Final Amount
			];

			histories.push(summaryRow);

			// Create Worksheet
			const ws = XLSX.utils.aoa_to_sheet(histories, header);

			// Create Workbook
			const wb = XLSX.utils.book_new();

			/* Add the worksheet to the workbook */
			XLSX.utils.book_append_sheet(wb, ws, 'Credits Histories');

			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);

			const fileName = `credits-${startDate}-to-${endDate}.xlsx`;

			// Export Excel File
			XLSX.writeFile(wb, fileName);
		},
		generateOtp() {
			this.isGeneratingOtp = true;
			this.isOtpVisible = true;

			const payloadJson = JSON.stringify(this.otpPayload);
			const params = {
				type: 'withdrawal',
				payload: payloadJson,
			};
			const callback = () => {
				this.isGeneratingOtp = false;
				// Start OTP Timer Interval
				this.startOtpTimer();
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				console.log(message);
				// this.$notify({
				// 	group: 'app',
				// 	type: 'error',
				// 	title: 'OTP Code',
				// 	text: message,
				// });
				this.isGeneratingOtp = false;
				this.startOtpTimer();
			};
			otpApi.generate(params, callback, errorCallback);
		},
		startOtpTimer() {
			this.otpTimerValue = parseInt(this.maxOtpTime);
			const otpInterval = setInterval(() => {
				this.otpTimerValue--;
				if (this.otpTimerValue === 0) {
					clearInterval(this.otpTimerInterval);
				}
			}, 1000);

			this.otpTimerInterval = otpInterval;
		},
		validateOtp() {
			this.isValidatingOtp = true;
			const code = this.otpCode.trim();
			const params = {
				code,
			};
			const callback = (response) => {
				console.log(response);
				this.isValidatingOtp = false;
				this.withdraw();
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'OTP Validation',
					text: message,
				});
				this.isValidatingOtp = false;
			};
			otpApi.validate(params, callback, errorCallback);
		},
		cancelWithdraw() {
			this.isWithdrawalVisible = false;
			this.resetOtp();
		},
		resetOtp() {
			if (this.otpTimerInterval) {
				clearInterval(this.otpTimerInterval);
				this.otpTimerInterval = null;
			}
			this.otpTimerValue = parseInt(this.maxOtpTime);
			// this.isOtpValid = false;
			this.isOtpVisible = false;
			this.isGeneratingOtp = false;
			this.otpCode = '';
		},
		getTypeLabel(type) {
			let label = type;
			if (type === 'cod_revenue') {
				label = 'COD Revenue';
			} else {
				label = toTitleCase(type.replace(/_/g, ' '));
			}
			return label;
		},
		checkPassword() {
			this.isCheckingPassword = true;

			const password = this.password.trim();

			const params = {
				password,
			};
			const callback = () => {
				this.isCheckingPassword = false;
				this.generateOtp();
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Password',
					text: message,
				});
				this.isCheckingPassword = false;
			};
			user.validatePassword(params, callback, errorCallback);
		},
		getStashBalance() {
			this.isFetchingStash = true;
			const callback = (response) => {
				this.isFetchingStash = false;
				const total = response.data;
				this.stashTotal = total;
			};
			const errorCallback = (e) => {
				this.isFetchingStash = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Stash Balance',
					text: message,
				});
			};
			walletStashApi.getTotal(callback, errorCallback);
		},
		fetchDataStats() {
			this.isFetchingBalanceByDate = true;

			const dateFormat = 'YYYY-MM-DD';

			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			const params = {
				since: startDate,
				until: endDate,
			};

			const callback = (response) => {
				const stats = response.data;
				this.balanceByDate = stats?.commissions;
				this.serviceFeeByDate = stats?.service_fee;
				this.productSourcingFeeByDate = stats?.product_sourcing_fee;
				this.handlingFeeByDate = stats?.handling_fee;
				this.revenueProductByDate = stats?.revenue_product;
				this.isFetchingBalanceByDate = false;
				this.isShowFilter = false;
			};

			const errorCallback = () => {
				this.isFetchingBalanceByDate = false;
			};

			if (this.source) {
				this.source.cancel('Operation canceled by the user.');
			}

			const CancelToken = axios.CancelToken;
			const source = CancelToken.source();
			this.source = source;

			const sourceToken = this.source.token;

			statsApi.getAppStatsNew(params, callback, errorCallback, sourceToken);
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			isAdmin: 'isAdmin',
			isOwner: 'isOwner',
			isUserCan: 'isUserCan',
			isHasFeature: 'isHasFeature',
			isTester: 'isTester',
			bankAccounts: 'bankAccounts',
			presetRangeLabel: 'presetRangeLabel',
			filterDatePresets: 'filterDatePresets',
			members: 'members/all',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		balanceDisplay() {
			return formatNumberPlain(this.balance);
		},
		balanceByDateDisplay() {
			return formatNumberPlain(this.balanceByDate);
		},
		serviceFeeDisplay() {
			return formatNumberPlain(this.serviceFee);
		},
		serviceFeeByDateDisplay() {
			return formatNumberPlain(this.serviceFeeByDate);
		},
		productSourcingFeeDisplay() {
			return formatNumberPlain(this.productSourcingFee);
		},
		productSourcingFeeByDateDisplay() {
			return formatNumberPlain(this.productSourcingFeeByDate);
		},
		commissionDisplay() {
			return formatNumberPlain(this.commission);
		},
		handlingFeeDisplay() {
			return formatNumberPlain(this.handlingFee);
		},
		handlingFeeByDateDisplay() {
			return formatNumberPlain(this.handlingFeeByDate);
		},
		revenueProductByDateDisplay() {
			return formatNumberPlain(this.revenueProductByDate);
		},
		revenueProductDisplay() {
			return formatNumberPlain(this.revenueProduct);
		},
		stashDisplay() {
			return formatNumberPlain(this.stashTotal);
		},
		isWithdrawValid() {
			return this.isWithdrawValueValid && !!this.bankAccount && this.password !== '';
		},
		isWithdrawValueValid() {
			return this.withdrawValue
				&& this.withdrawValue > 0
				&& this.withdrawValue >= this.withdrawTreshold
				&& this.withdrawValue <= this.balance;
		},
		isFilterOn() {
			return this.status !== 'all' || this.type !== 'all';
		},
		bankAccountsOptions() {
			const bankAccounts = this.bankAccounts.map((bankAccount) => {
				bankAccount.id = `${bankAccount.name}-${bankAccount.account_number}`;
				bankAccount.text = `${bankAccount.label} - ${bankAccount.account_name} - ${bankAccount.account_number}`;
				return bankAccount;
			});
			return bankAccounts;
		},
		withdrawDescription() {
			const value = formatNumberPlain(this.withdrawValue);
			const description = `Withdraw ${value} to ${this.bankAccount.text}`;
			return description;
		},
		isCanWithdraw() {
			return this.isOwner || this.isUserCan('wallet_withdraw');
			// return false;
		},
		sensoredEmail() {
			const email = this?.user?.email ?? '';
			const sensoredEmail = this.isOwner ? sensorEmailAddress(email) : 'Owner account ini';
			return sensoredEmail;
		},
		otpTimer() {
			const timerLabel = generateTimerLabel(this.otpTimerValue);
			return timerLabel;
		},
		otpPayload() {
			const destination = this.bankAccount ? this.bankAccount.text : '';
			const payload = {
				value: this.withdrawValue,
				destination,
			};
			return payload;
		},
		isOtpValid() {
			return this.otpCode && this.otpCode.length === 6;
		},
		isWdDisabled() {
			return this.isHasFeature('withdraw_disabled');
		},
	},
	watch: {
		user() {
			this.init();
		},
	},
	created() {
		if (this.user) {
			this.init();
		}
		window.addEventListener('scroll', this.handleScroll);
		document.addEventListener('scroll', this.onDocumentScroll, true);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
		document.removeEventListener('scroll', this.onDocumentScroll, true);
	},
};
</script>

<style scoped>
.mh-120 {
	min-height: 120px;
}
</style>
